import { useMemo, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { v4 } from 'uuid';
import { RequestParameters } from '~/backend/backend-client';
import { Image3D } from '~/domain/patient/image3d';
import { getStructureSetRepository } from '~/domain/ServiceContainer';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { environmentState } from '~/state/atoms/engineAtoms';
import { primaryImageState } from '~/state/atoms/image/imageAtoms';
import { Job } from '~/state/atoms/jobAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import {
    WorkflowTypes,
    pseudonymizeState,
    workflowModalState,
} from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';
import { transformTaskToGenerationModel } from '~/utils/transforms/taskTransforms';
import { NewJobData, useJobManagement } from '../../useJobManagement';
import useAnonymizer from '../useAnonymizer';
import { useRegistrationBackend, useSessionID } from '../useBackend';

export type BackendContourPropagationTaskInput = {
    selectedModel: PredictionModel;
    targetImageSeriesUid?: string;
    selectedStructures?: string[];
    customizationName?: string;
};

/**
 * A React hook for registering and propagating structures between images based on the selected model.
 * @returns {Object} - An object containing the start function and submission state.
 */
export function useRegistration() {
    const primaryImage = useRecoilValue(primaryImageState);

    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const environment = useRecoilValue(environmentState);
    const pseudonymize = useRecoilValue(pseudonymizeState);
    const setWorkflowModalState = useSetRecoilState(workflowModalState);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const patient = useSelectedPatient();
    const registrationClient = useRegistrationBackend();
    const { addJob, updateJob } = useJobManagement();
    const { anonymizer, anonymizeBlobs, reidentifyFiles } = useAnonymizer();
    const { resetSessionID, sessionID } = useSessionID();
    const { processCreatedFiles } = useProcessDicomFiles();
    const { initializeEnvironment } = useViewportEnvironment();

    const availableImages = useMemo(
        () => patient?.images.filter((image) => image !== primaryImage) || [],
        [primaryImage, patient]
    );

    const resolveTargetImage = (
        targetImageUid?: string
    ): Image3D | undefined => {
        if (!targetImageUid) {
            return undefined;
        }
        return availableImages.find((img) => img.seriesUid === targetImageUid);
    };

    const handleProcessedFiles = async (job: Job) => {
        if (job.results && job.results?.length > 0) {
            const finalFiles = job.payload?.pseudonymized
                ? await reidentifyFiles(job.results)
                : job.results;

            if (finalFiles) {
                const generationModel = transformTaskToGenerationModel(
                    job.task
                );
                const processedItems = await processCreatedFiles(
                    finalFiles,
                    generationModel
                );
                if (processedItems) {
                    updateJob(job.id, { isImported: true });
                    const resultStructureSet = processedItems.structureSets[0];
                    const targetRegistration = processedItems.registrations[0];
                    const targetImage = resultStructureSet.image;
                    if (
                        resultStructureSet &&
                        targetImage &&
                        targetRegistration
                    ) {
                        initializeEnvironment({
                            structureSets: [resultStructureSet],
                            images: [targetImage],
                            registrations: [targetRegistration],
                            doses: [],
                            patients: [],
                            interpolatedImages: [],
                            skippedSeries: [],
                        });
                    }
                }
            } else {
                handleError('Re-identification failed for one or more files.');
            }
        }
    };

    const startProcessing = async (
        input: BackendContourPropagationTaskInput
    ) => {
        if (isSubmitting) {
            return;
        }

        const blobsToUpload: Blob[] = [];
        const registeredImage = resolveTargetImage(input.targetImageSeriesUid);
        if (
            !primaryImage ||
            !registeredImage ||
            !input.selectedModel ||
            !currentStructureSet
        ) {
            handleError('Missing required data for registration');
            return;
        }

        setIsSubmitting(true);
        try {
            resetSessionID();
            const taskUID = `${sessionID}.${v4().split('-')[0]}`;

            const primaryImageBlobs = Array.from(
                primaryImage.getSlicesAsDicomBlobs()
            );
            const targetImageBlobs = Array.from(
                registeredImage.getSlicesAsDicomBlobs()
            );
            const totalSlices =
                primaryImageBlobs.length + targetImageBlobs.length + 1; // adds structure set slice

            const modelIdentifier = input.customizationName
                ? `${input.selectedModel.name}.${input.customizationName}`
                : input.selectedModel.name;

            const backendParameters: RequestParameters = {
                action: modelIdentifier,
                movingSeriesUID: primaryImage?.seriesUid || '',
                fixedSeriesUID: input.targetImageSeriesUid || '',
                nExpectedFiles: totalSlices.toString(),
                taskUID,
                sessionId: sessionID,
            };

            const primaryBlobsToUpload = pseudonymize
                ? await anonymizeBlobs(taskUID, primaryImageBlobs)
                : primaryImageBlobs;
            const targetBlobsToUpload = pseudonymize
                ? await anonymizeBlobs(taskUID, targetImageBlobs)
                : targetImageBlobs;

            if (anonymizer.UIDMapping) {
                if (
                    backendParameters.movingSeriesUID &&
                    backendParameters.movingSeriesUID in anonymizer.UIDMapping
                ) {
                    backendParameters.movingSeriesUID =
                        anonymizer.UIDMapping[
                            backendParameters.movingSeriesUID
                        ];
                }

                if (
                    backendParameters.fixedSeriesUID &&
                    backendParameters.fixedSeriesUID in anonymizer.UIDMapping
                ) {
                    backendParameters.fixedSeriesUID =
                        anonymizer.UIDMapping[backendParameters.fixedSeriesUID];
                }
            }

            if (primaryBlobsToUpload) {
                blobsToUpload.push(...primaryBlobsToUpload);
            } else {
                handleError('Anonymization failed for primary image files.');
                return;
            }

            if (targetBlobsToUpload) {
                blobsToUpload.push(...targetBlobsToUpload);
            } else {
                handleError('Anonymization failed for target image files.');
                return;
            }

            if (currentStructureSet && environment) {
                environment.saveChanges();
                const repository = getStructureSetRepository();
                const groundTruthStructureSet = repository.getGroundTruth(
                    currentStructureSet.dicomUid
                );
                const structureSetBlob = groundTruthStructureSet.exportWith(
                    groundTruthStructureSet.roiArray
                        .filter((roi) =>
                            input.selectedStructures?.includes(roi.name)
                        )
                        .map((roi) => roi.roiNumber)
                );
                const structureSetBlobToUpload = pseudonymize
                    ? await anonymizeBlobs(taskUID, [structureSetBlob])
                    : [structureSetBlob];
                if (structureSetBlobToUpload) {
                    blobsToUpload.push(...structureSetBlobToUpload);
                } else {
                    handleError('Anonymization failed for structure set.');
                    return;
                }
            }

            const newJobDetails: NewJobData = {
                id: sessionID,
                name: 'Adapt+',
                totalItems: blobsToUpload.length,
                type: WorkflowTypes.ADAPT,
                client: registrationClient,
                onLoad: handleProcessedFiles,
                payload: {
                    filesToUpload: blobsToUpload.map((file) => ({ file })),
                    backendParameters,
                    pseudonymized: pseudonymize,
                },
                task: {
                    model: input.selectedModel,
                },
            };

            addJob(newJobDetails);
            setWorkflowModalState((state) => ({
                ...state,
                open: false,
            }));
        } catch (error) {
            handleError(error, 'Failed to start registration task.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return { startProcessing, isSubmitting };
}
